import Seo from '@/seo/Seo';
import { Typography } from '@/shared/ui/typography';
import { useServices } from '@/store/api/services';
import cn from 'classnames';
import { motion, useInView } from 'framer-motion';
import { useTranslation } from 'next-i18next';
import { ContactUs } from './ui/ContactUs';
import { MainTitlePage } from './ui/MainTitlePage';
import { OurServices } from './ui/OurServices';
import { AnimatedTitle } from '@/shared/ui/animated';
import { useRef } from 'react';
import Image from 'next/image';
import { YandexMetrika } from '@/widgets/metrika';

const advantages = [
  {
    title: '97%',
    key: 'accuracy',
    description: 'services.key_numbers.accuracy',
    className:
      'dk:w-[202px] tb:w-[148px] dk:mr-[86px] tb:mr-[52px] mb:w-[154px] mb:mr-[18px] mb:max-tb:pl-[5px]',
  },
  {
    title: '2000+',
    key: 'performance',
    description: 'services.key_numbers.performance',
    className:
      'dk:w-[241px] tb:w-[193px] dk:mr-[140px] tb:mr-[83px] mb:w-[179px] mb:max-tb:pl-[6px]',
  },
  {
    title: '98%',
    key: 'timeliness',
    description: 'services.key_numbers.timeliness',
    className:
      'dk:w-[169px] tb:w-[177px] mb:w-[172px] dk:mr-[136px] tb:mr-[29px] mb:max-tb:pl-[5px]',
  },
  {
    title: '2000+',
    key: 'positions',
    description: 'services.key_numbers.positions',
    className: 'dk:w-[245px] tb:w-[198px] mb:w-[183px] mb:max-tb:pl-[6px]',
  },
];

export const ServicesPage = () => {
  const { seo, key_numbers } = useServices();
  const { t, i18n } = useTranslation('common');

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <>
      {/* Хз где description найти */}
      <Seo seo={seo} />
      <YandexMetrika id={96166340} />
      <MainTitlePage />
      <section
        className={cn(
          'tb:container mb:max-tb:max-w-[355px] container flex flex-wrap',
          'dk:pt-[41px] dk:pb-[86px] tb:pt-6 tb:pb-[49px] mb:py-5 dk:gap-y-10 tb:gap-y-6 mb:gap-y-6',
        )}
      >
        {advantages.map((item, index) => (
          <div className={cn(item.className)} key={item.description}>
            <motion.div className="relative">
              <Typography variant="h3">{item.title}</Typography>
              <Typography
                variant="textX"
                className="mb:max-dk:text-textS tb:max-dk:text-textS"
              >
                {key_numbers &&
                  key_numbers[item.key as keyof typeof key_numbers]}
              </Typography>
              <motion.div
                className="absolute inset-[-2px] bg-white"
                style={{
                  clipPath: 'polygon(25% 0%, 100% 0%, 100% 100%, 0% 100%)',
                }}
                initial={{ left: '-40%' }}
                whileInView={{ left: '100%' }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, ease: 'easeInOut' }}
              />
            </motion.div>
          </div>
        ))}
      </section>
      <OurServices />
      {i18n.language === 'ru' && (
        <div className="bg-blue-dark dk:pl-40 py-8">
          <div ref={ref} className="container relative">
            <Typography
              variant="h2"
              className={cn(
                'dk:text-tb_h2 relative flex w-full flex-col overflow-hidden uppercase text-white',
              )}
            >
              <motion.span
                className="flex w-full"
                initial={{ clipPath: 'inset(0 100% 0 0)' }}
                animate={{
                  clipPath: isInView ? 'inset(0 0 0 0)' : 'inset(0 100% 0 0)',
                }}
                transition={{ duration: 1 }}
              >
                <AnimatedTitle
                  className="self-end"
                  isInView={isInView}
                  desktop={{ initialX: 100 }}
                  tablet={{ initialX: 60 }}
                  delay={1}
                >
                  Логистика
                </AnimatedTitle>
              </motion.span>
              <motion.span
                className="dk:pl-[244.5px] tb:pl-[102px] flex w-full"
                initial={{ clipPath: 'inset(0 100% 0 0)' }}
                animate={{
                  clipPath: isInView ? 'inset(0 0 0 0)' : 'inset(0 100% 0 0)',
                }}
                transition={{ duration: 1 }}
              >
                <AnimatedTitle
                  className="self-end"
                  isInView={isInView}
                  desktop={{ initialX: -75 }}
                  tablet={{ initialX: -40 }}
                  delay={1}
                >
                  Настроенная на вас
                </AnimatedTitle>
              </motion.span>
              <motion.span
                initial={{ clipPath: 'inset(0 100% 0 0)' }}
                animate={{
                  clipPath: isInView ? 'inset(0 0 0 0)' : 'inset(0 100% 0 0)',
                }}
                transition={{ ease: 'easeInOut', duration: 0.4, delay: 1.7 }}
                className={cn(
                  'bg-blue-dark absolute',
                  'dk:h-[53px] dk:w-[44px] tb:h-[53px] tb:w-[44px] mb:h-[32px] mb:w-[28px]',
                  'tb:left-[786.5px] tb:bottom-[16px] dk:left-[928.5px] dk:bottom-[16px] mb:left-[98.5px] mb:bottom-[12px]',
                )}
              >
                <Image alt="" src={'/images/letters/letterA.svg'} fill />
              </motion.span>
            </Typography>
          </div>
        </div>
      )}
      <ContactUs />
    </>
  );
};
